import { friendlyProblem, logClientError, networkProblem } from "@stagvista/ui";

import { sidecarBaseUrl } from "./sidecar";

/**
 * Mirrors `apps/desktop`'s own `SidecarHttpClient` — same RFC 9457
 * Problem Details error shape, same bearer-token convention — duplicated
 * rather than imported, since the two are separate apps with no shared
 * package for this (the established "local, typing-only redeclaration"
 * convention this codebase already uses everywhere else, just applied to
 * a small client instead of just types).
 */
export interface ProblemDetails {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail?: string;
  readonly instance?: string;
  readonly [key: string]: unknown;
}

export class SidecarHttpError extends Error {
  constructor(readonly problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = "SidecarHttpError";
  }
}

export class SidecarHttpClient {
  constructor(private readonly getToken: () => string | null) {}

  async request<T>(method: "GET" | "POST" | "DELETE", path: string, body?: unknown): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers.authorization = `Bearer ${token}`;
    if (body !== undefined) headers["content-type"] = "application/json";

    const response = await this.fetchFriendly(method, `${sidecarBaseUrl()}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const problem = (await response.json().catch(() => null)) as ProblemDetails | null;
      throw this.toError(
        problem ?? {
          type: "about:blank",
          title: response.statusText || "Request failed",
          status: response.status,
        },
        method,
        path,
      );
    }

    // Some `200`-status action routes intentionally send no body at all
    // (see `apps/desktop`'s own identical fix, and `delegated.route.ts`'s
    // own doc comment on this failure mode) — treat any genuinely empty
    // body as `undefined` regardless of status code, rather than assuming
    // every non-204 success has JSON to parse. `response.json()` throws
    // `SyntaxError: Unexpected end of JSON input` on an empty body, which
    // is not a recoverable network condition.
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  /** `fetch`, but a request that never got an answer throws a readable error (the raw browser text is kept for the log, not the screen). */
  private async fetchFriendly(method: string, url: string, init: RequestInit): Promise<Response> {
    try {
      return await fetch(url, init);
    } catch (cause) {
      const problem = networkProblem(cause, { target: "local", method, url });
      logClientError(problem, { target: "local", method, url });
      throw new SidecarHttpError(problem);
    }
  }

  /** A failed response as a SidecarHttpError whose text is readable and carries a reference code. */
  private toError(problem: ProblemDetails, method: string, path: string): SidecarHttpError {
    const friendly = friendlyProblem(problem);
    logClientError(friendly, { target: "local", method, url: `${sidecarBaseUrl()}${path}` });
    return new SidecarHttpError(friendly);
  }
}
